import { template as template_6e2b2b9a96ce4054bb7967fbcfcd007a } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { task as trackedTask } from 'reactiveweb/ember-concurrency';
import moment from 'moment-timezone';
import { formatStateColumnData } from 'eflex/util/bi-format-oee-state-cell';
import { waitFor } from '@ember/test-waiters';
import { clone } from 'ramda';
import { t } from 'ember-intl';
import TabulatorTable from 'eflex/components/tabulator-table';
import BiNoDataPlaceholder from 'eflex/components/bi/no-data-placeholder';
import Spinner from 'eflex/components/spinner';
export default class OeeStatesTable extends Component {
    @service
    queryRunner;
    @service
    intl;
    @service
    store;
    get params() {
        return {
            stationIds: this.args.stationIds,
            startDate: this.args.startDate,
            endDate: this.args.endDate,
            shiftNames: this.args.shiftNames,
            buttonIds: this.args.buttonIds,
            states: this.args.states
        };
    }
    get tableConfiguration() {
        return {
            columns: [
                {
                    title: this.intl.t('startTime'),
                    field: 'startDate',
                    formatter: 'textarea',
                    resizable: true
                },
                {
                    title: this.intl.t('finishTime'),
                    field: 'endDate',
                    formatter: 'textarea',
                    resizable: true
                },
                {
                    title: this.intl.t('scheduler.duration'),
                    field: 'duration',
                    formatter: 'textarea',
                    resizable: true
                },
                {
                    title: this.intl.t('username'),
                    field: 'username',
                    resizable: true
                },
                {
                    title: this.intl.t('state'),
                    field: 'state',
                    formatter: (cell)=>formatStateColumnData(cell),
                    hozAlign: 'center',
                    vertAlign: 'center',
                    width: 125,
                    resizable: true,
                    tooltip: false
                }
            ],
            childConfiguration: this.childConfiguration,
            options: {
                paginationSize: false,
                groupBy: 'stationId',
                groupToggleElement: 'header',
                groupHeader: (value)=>{
                    return this.store.peekRecord('station', value)?.name.toUpperCase();
                }
            }
        };
    }
    get childConfiguration() {
        return {
            columns: [
                {
                    title: this.intl.t('area'),
                    field: 'areaName',
                    formatter: 'textarea',
                    resizable: true,
                    headerSort: false
                },
                {
                    title: this.intl.t('group'),
                    field: 'groupName',
                    formatter: 'textarea',
                    resizable: true,
                    headerSort: false
                },
                {
                    title: this.intl.t('bi.chartLabel.oeeFaultCodes'),
                    formatter: 'textarea',
                    field: 'faultCode',
                    resizable: true,
                    headerSort: false
                },
                {
                    title: this.intl.t('bi.chartLabel.oeeFaultMessages'),
                    field: 'faultDescription',
                    formatter: 'textarea',
                    resizable: true,
                    headerSort: false
                }
            ]
        };
    }
    getOeeStateData = task({
        drop: true
    }, waitFor(async (params = {})=>{
        let sortStep;
        if (params.sort?.length > 0) {
            const dir = params.sort[0].dir === 'asc' ? 1 : -1;
            const field = params.sort[0].field;
            sortStep = {
                [field]: dir
            };
        } else {
            sortStep = {
                endDate: -1
            };
        }
        const query = await this.queryRunner.queryWithParams.perform(this.params, [
            {
                $skip: params.skip ?? 0
            },
            {
                $limit: params.size ?? 100_000
            },
            {
                $match: {
                    endDate: {
                        $ne: null
                    }
                }
            },
            {
                $lookup: {
                    from: 'LocationBases',
                    localField: 'station._id',
                    foreignField: '_id',
                    as: 'station'
                }
            },
            {
                $unwind: {
                    path: '$station'
                }
            },
            {
                $addFields: {
                    areaId: {
                        $toObjectId: {
                            $arrayElemAt: [
                                {
                                    $split: [
                                        '$station.path',
                                        '#'
                                    ]
                                },
                                0
                            ]
                        }
                    },
                    groupId: {
                        $toObjectId: {
                            $arrayElemAt: [
                                {
                                    $split: [
                                        '$station.path',
                                        '#'
                                    ]
                                },
                                1
                            ]
                        }
                    }
                }
            },
            {
                $lookup: {
                    from: 'LocationBases',
                    localField: 'areaId',
                    foreignField: '_id',
                    as: 'area'
                }
            },
            {
                $unwind: {
                    path: '$area'
                }
            },
            {
                $lookup: {
                    from: 'LocationBases',
                    localField: 'groupId',
                    foreignField: '_id',
                    as: 'group'
                }
            },
            {
                $unwind: {
                    path: '$group'
                }
            },
            {
                $project: {
                    _id: 0,
                    areaName: {
                        $last: '$area.captions.text'
                    },
                    groupName: {
                        $last: '$group.captions.text'
                    },
                    stationId: '$station._id',
                    stationName: {
                        $last: '$station.captions.text'
                    },
                    state: true,
                    userName: true,
                    startDate: true,
                    endDate: true,
                    duration: {
                        $dateDiff: {
                            startDate: '$startDate',
                            endDate: '$endDate',
                            unit: 'millisecond'
                        }
                    },
                    faultCodes: true,
                    customButtonColor: true
                }
            },
            {
                $sort: sortStep
            }
        ], true);
        const oeeStates = await this.queryRunner.runQuery.perform('OeeStates', query, true);
        const rows = oeeStates?.result?.filter((row)=>row.endDate).map((state)=>({
                stationId: state.stationId,
                stationName: state.stationName,
                startDate: moment(state.startDate).format('MM/DD/YYYY hh:mm A'),
                endDate: moment(state.endDate).format('MM/DD/YYYY hh:mm A'),
                duration: moment.duration(state.duration, 'milliseconds').format('d[d] h[h] m[m] s[s]', {
                    precision: 1,
                    trim: 'both mid'
                }),
                username: state.userName,
                state,
                isParentRow: true,
                children: [
                    {
                        areaName: state.areaName,
                        groupName: state.groupName,
                        faultCode: state.faultCodes?.codes?.map((faultCode)=>`${faultCode.code} - ${faultCode.description}`).join(', '),
                        faultDescription: state.faultCodes?.note
                    }
                ]
            }));
        return {
            count: oeeStates.meta?.count,
            data: rows,
            exportData: ()=>this.formatExportData(clone(rows))
        };
    }));
    oeeStateData = trackedTask(this, this.getOeeStateData, ()=>[
            this.params,
            this.args.refresher
        ]);
    formatExportData(data) {
        return data.map((row)=>{
            const { stationName, startDate, endDate, duration, username, state } = row;
            const { areaName, groupName, faultCode, faultDescription } = row.children[0];
            return {
                stationName,
                areaName,
                groupName,
                startDate,
                endDate,
                duration,
                username,
                state: state.state,
                faultCode: faultCode ?? '',
                faultDescription: faultDescription ?? ''
            };
        });
    }
    static{
        template_6e2b2b9a96ce4054bb7967fbcfcd007a(`
    <div class="bi-tabulator component-bi-oee-states-table d-flex flex-column h-100" ...attributes>
      {{#if this.oeeStateData.isRunning}}
        <Spinner @belowSidePanel={{true}} />
      {{else if this.oeeStateData.value.count}}
        <TabulatorTable
          @tableConfiguration={{this.tableConfiguration}}
          @getRowData={{this.getOeeStateData.perform}}
          @updateDataAttr={{this.params}}
          @exportFilename={{t 'bi.chartLabel.oeeStatesTable'}}
          @exportDataFunction={{this.oeeStateData.value.exportData}}
        />
      {{else}}
        <BiNoDataPlaceholder />
      {{/if}}
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
